import { useState, useEffect, Dispatch, SetStateAction } from 'react';
// @ts-ignore
import { JsonStorage } from '/src/utils/storage';

interface UsePersistedStateParams {
  key: string;
  ttl?: number;
}

type UsePersistedStateReturn<T> = [T, Dispatch<SetStateAction<T>>];

function usePersistedState<T>(
  initialValue: T,
  paramsOrKey: string | UsePersistedStateParams
): UsePersistedStateReturn<T> {
  let key: string;
  let ttl: number | undefined;

  if (typeof paramsOrKey === 'object') {
    ({ key, ttl } = paramsOrKey);
  } else {
    key = paramsOrKey;
  }

  if (!key) {
    throw new Error('Key is required for persisted states.');
  }

  const [state, setState] = useState<T>(() => {
    const stored = JsonStorage.get(key) as T | null;
    return stored ?? initialValue;
  });

  useEffect(() => {
    const newValue = (JsonStorage.get(key) as T | null) ?? initialValue;
    if (!state || typeof newValue !== 'object' || JSON.stringify(newValue) !== JSON.stringify(state)) {
      setState(newValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    JsonStorage.set({
      key,
      value: state,
      ttl,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, ttl]);

  return [state, setState];
}

export default usePersistedState;
